import { useState, type FormEvent } from "react";
import type { AddMemberOutcome } from "../../shared/api-types";
import { apiError, useT } from "../i18n";
import { apiJson } from "../lib/api";
import { Button, Input, Modal, Select } from "./ui";

/**
 * Person in einen Account holen — Admin (eigener Account) und Betreiber
 * (beliebiger Account) nutzen denselben Dialog, nur die URL unterscheidet sich.
 * Bestehender Zugang → sofort Mitglied; neue E-Mail → Einladung per Mail.
 */
export function AddMemberModal({
  open,
  onClose,
  onAdded,
  url,
  title,
}: {
  open: boolean;
  onClose: () => void;
  onAdded: (outcome: AddMemberOutcome) => void;
  url: string;
  title: string;
}) {
  const t = useT();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"owner" | "member">("member");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setEmail("");
    setRole("member");
    setError(null);
  }

  function close() {
    reset();
    onClose();
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await apiJson<{ result: AddMemberOutcome }>(url, {
        method: "POST",
        json: { email, role },
      });
      reset();
      onAdded(res.result);
    } catch (err) {
      setError(apiError(t, err instanceof Error ? err.message : "unknown"));
    }
    setBusy(false);
  }

  const label = "mb-1.5 block text-sm font-medium text-zinc-700 dark:text-zinc-300";

  return (
    <Modal open={open} onClose={close} title={title}>
      <form onSubmit={(e) => void submit(e)} className="space-y-4">
        <p className="text-sm text-zinc-500 dark:text-zinc-400">{t.members.inviteHint}</p>
        <label className="block">
          <span className={label}>{t.members.email}</span>
          <Input
            type="email"
            className="w-full"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </label>
        <label className="block">
          <span className={label}>{t.members.role}</span>
          <Select
            className="w-full"
            value={role}
            onChange={(e) => setRole(e.target.value as "owner" | "member")}
          >
            <option value="member">{t.members.roleMember}</option>
            <option value="owner">{t.members.roleOwner}</option>
          </Select>
        </label>
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={close}>
            {t.common.cancel}
          </Button>
          <Button type="submit" disabled={busy}>
            {t.members.add}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
